// Keyword optimization utilities for SEO content analysis

export interface KeywordAnalysis {
  keyword: string;
  density: number;
  count: number;
  positions: number[];
  prominence: number;
  variations: string[];
}

export interface ContentAnalysis {
  wordCount: number;
  readabilityScore: number;
  keywordAnalysis: KeywordAnalysis[];
  semanticKeywords: string[];
  headingStructure: HeadingAnalysis[];
  internalLinks: LinkAnalysis[];
  recommendations: string[];
}

export interface HeadingAnalysis {
  level: number;
  text: string;
  position: number;
  hasKeyword: boolean;
  keywords: string[];
}

export interface LinkAnalysis {
  text: string;
  url: string;
  isInternal: boolean;
  hasKeyword: boolean;
  keywords: string[];
}

/**
 * Analyze keyword density in content
 */
export const analyzeKeywordDensity = (
  content: string,
  targetKeywords: string[]
): KeywordAnalysis[] => {
  const cleanContent = content.toLowerCase().replace(/[^\w\s]/g, ' ');
  const words = cleanContent.split(/\s+/).filter(Boolean);
  const totalWords = words.length;

  return targetKeywords.map(keyword => {
    const keywordLower = keyword.toLowerCase();
    const keywordWords = keywordLower.split(/\s+/);
    const keywordLength = keywordWords.length;
    
    let count = 0;
    const positions: number[] = [];
    const variations: string[] = [];

    // Find exact matches and variations
    for (let i = 0; i <= words.length - keywordLength; i++) {
      const phrase = words.slice(i, i + keywordLength).join(' ');
      
      if (phrase === keywordLower) {
        count++;
        positions.push(i);
      } else if (isKeywordVariation(phrase, keywordLower)) {
        variations.push(phrase);
        count += 0.5; // Partial credit for variations
        positions.push(i);
      }
    }

    const density = totalWords > 0 ? (count / totalWords) * 100 : 0;
    const prominence = calculateKeywordProminence(positions, totalWords);

    return {
      keyword,
      density,
      count: Math.floor(count),
      positions,
      prominence,
      variations: [...new Set(variations)],
    };
  });
};

/**
 * Check if a phrase is a variation of the target keyword
 */
const isKeywordVariation = (phrase: string, target: string): boolean => {
  const phraseWords = phrase.split(/\s+/);
  const targetWords = target.split(/\s+/);
  
  if (phraseWords.length !== targetWords.length) return false;
  
  let matches = 0;
  for (let i = 0; i < phraseWords.length; i++) {
    if (phraseWords[i] === targetWords[i] || 
        isWordVariation(phraseWords[i], targetWords[i])) {
      matches++;
    }
  }
  
  return matches / targetWords.length >= 0.8; // 80% similarity threshold
};

/**
 * Check if two words are variations (plurals, tenses, etc.)
 */
const isWordVariation = (word1: string, word2: string): boolean => {
  // Simple stemming rules
  const stem1 = word1.replace(/(ing|ed|s|es|er|est)$/, '');
  const stem2 = word2.replace(/(ing|ed|s|es|er|est)$/, '');
  
  return stem1 === stem2 || 
         Math.abs(stem1.length - stem2.length) <= 2 && 
         levenshteinDistance(stem1, stem2) <= 2;
};

/**
 * Calculate keyword prominence based on positions
 */
const calculateKeywordProminence = (positions: number[], totalWords: number): number => {
  if (positions.length === 0) return 0;
  
  let prominence = 0;
  
  positions.forEach(position => {
    // Higher score for keywords appearing earlier in content
    const positionScore = Math.max(0, 1 - (position / totalWords));
    prominence += positionScore;
  });
  
  return prominence / positions.length;
};

/**
 * Generate semantic keywords using LSI (Latent Semantic Indexing) approach
 */
export const generateSemanticKeywords = (
  content: string,
  primaryKeywords: string[]
): string[] => {
  const semanticMap: Record<string, string[]> = {
    'ai': ['artificial intelligence', 'machine learning', 'deep learning', 'neural networks', 'automation', 'algorithms'],
    'netsuite': ['erp', 'enterprise resource planning', 'business management', 'cloud software', 'oracle'],
    'shopify': ['ecommerce', 'online store', 'retail', 'commerce platform', 'dropshipping'],
    'development': ['programming', 'coding', 'software', 'application', 'web development', 'custom solutions'],
    'integration': ['api', 'connectivity', 'data sync', 'workflow', 'automation', 'middleware'],
    'analytics': ['data analysis', 'reporting', 'business intelligence', 'metrics', 'insights', 'dashboard'],
  };

  const semanticKeywords = new Set<string>();
  
  primaryKeywords.forEach(keyword => {
    const keywordLower = keyword.toLowerCase();
    
    // Find semantic matches
    Object.entries(semanticMap).forEach(([key, values]) => {
      if (keywordLower.includes(key) || values.some(v => keywordLower.includes(v))) {
        values.forEach(value => semanticKeywords.add(value));
      }
    });
  });

  // Extract co-occurring terms from content
  const words = content.toLowerCase().match(/\b\w{4,}\b/g) || [];
  const wordFreq = new Map<string, number>();
  
  words.forEach(word => {
    wordFreq.set(word, (wordFreq.get(word) || 0) + 1);
  });

  // Add frequently occurring relevant terms
  Array.from(wordFreq.entries())
    .filter(([word, freq]) => freq >= 3 && word.length >= 4)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 10)
    .forEach(([word]) => semanticKeywords.add(word));

  return Array.from(semanticKeywords);
};

/**
 * Analyze heading structure for SEO
 */
export const analyzeHeadingStructure = (
  content: string,
  targetKeywords: string[]
): HeadingAnalysis[] => {
  const headingRegex = /<h([1-6])[^>]*>(.*?)<\/h[1-6]>/gi;
  const headings: HeadingAnalysis[] = [];
  let match;

  while ((match = headingRegex.exec(content)) !== null) {
    const level = parseInt(match[1]);
    const text = match[2].replace(/<[^>]*>/g, '').trim();
    const position = match.index;
    
    const keywords = targetKeywords.filter(keyword =>
      text.toLowerCase().includes(keyword.toLowerCase())
    );

    headings.push({
      level,
      text,
      position,
      hasKeyword: keywords.length > 0,
      keywords,
    });
  }

  return headings;
};

/**
 * Analyze internal links for SEO
 */
export const analyzeInternalLinks = (
  content: string,
  targetKeywords: string[],
  baseUrl: string
): LinkAnalysis[] => {
  const linkRegex = /<a[^>]+href=["']([^"']+)["'][^>]*>(.*?)<\/a>/gi;
  const links: LinkAnalysis[] = [];
  let match;

  while ((match = linkRegex.exec(content)) !== null) {
    const url = match[1];
    const text = match[2].replace(/<[^>]*>/g, '').trim();
    
    const isInternal = url.startsWith('/') || url.includes(baseUrl);
    const keywords = targetKeywords.filter(keyword =>
      text.toLowerCase().includes(keyword.toLowerCase())
    );

    links.push({
      text,
      url,
      isInternal,
      hasKeyword: keywords.length > 0,
      keywords,
    });
  }

  return links;
};

/**
 * Calculate readability score (Flesch Reading Ease)
 */
export const calculateReadabilityScore = (content: string): number => {
  const sentences = content.split(/[.!?]+/).filter(s => s.trim().length > 0);
  const words = content.match(/\b\w+\b/g) || [];
  const syllables = words.reduce((total, word) => total + countSyllables(word), 0);

  if (sentences.length === 0 || words.length === 0) return 0;

  const avgSentenceLength = words.length / sentences.length;
  const avgSyllablesPerWord = syllables / words.length;

  const score = 206.835 - (1.015 * avgSentenceLength) - (84.6 * avgSyllablesPerWord);
  return Math.max(0, Math.min(100, score));
};

/**
 * Count syllables in a word (approximation)
 */
const countSyllables = (word: string): number => {
  word = word.toLowerCase();
  if (word.length <= 3) return 1;
  
  const vowels = word.match(/[aeiouy]+/g);
  let count = vowels ? vowels.length : 1;
  
  if (word.endsWith('e')) count--;
  if (word.endsWith('le') && word.length > 2) count++;
  
  return Math.max(1, count);
};

/**
 * Generate SEO recommendations based on content analysis
 */
export const generateSEORecommendations = (
  analysis: ContentAnalysis,
  targetKeywords: string[]
): string[] => {
  const recommendations: string[] = [];

  // Word count recommendations
  if (analysis.wordCount < 300) {
    recommendations.push('Content is too short. Aim for at least 300 words for better SEO.');
  } else if (analysis.wordCount > 2000) {
    recommendations.push('Content is quite long. Consider breaking it into multiple pages or sections.');
  }

  // Keyword density recommendations
  analysis.keywordAnalysis.forEach(keyword => {
    if (keyword.density < 0.5) {
      recommendations.push(`Keyword "${keyword.keyword}" density is too low (${keyword.density.toFixed(1)}%). Aim for 0.5-2.5%.`);
    } else if (keyword.density > 3) {
      recommendations.push(`Keyword "${keyword.keyword}" density is too high (${keyword.density.toFixed(1)}%). Reduce to avoid keyword stuffing.`);
    }

    if (keyword.prominence < 0.3) {
      recommendations.push(`Consider placing keyword "${keyword.keyword}" earlier in the content for better prominence.`);
    }
  });

  // Heading structure recommendations
  const h1Count = analysis.headingStructure.filter(h => h.level === 1).length;
  if (h1Count === 0) {
    recommendations.push('Add an H1 heading with your primary keyword.');
  } else if (h1Count > 1) {
    recommendations.push('Use only one H1 heading per page.');
  }

  const headingsWithKeywords = analysis.headingStructure.filter(h => h.hasKeyword).length;
  if (headingsWithKeywords === 0) {
    recommendations.push('Include target keywords in your headings (H1-H3).');
  }

  // Readability recommendations
  if (analysis.readabilityScore < 30) {
    recommendations.push('Content is difficult to read. Use shorter sentences and simpler words.');
  } else if (analysis.readabilityScore > 90) {
    recommendations.push('Content might be too simple. Consider adding more detailed explanations.');
  }

  // Internal linking recommendations
  const internalLinksWithKeywords = analysis.internalLinks.filter(l => l.isInternal && l.hasKeyword).length;
  if (internalLinksWithKeywords < 2) {
    recommendations.push('Add more internal links with keyword-rich anchor text.');
  }

  // Semantic keywords recommendations
  if (analysis.semanticKeywords.length < 5) {
    recommendations.push('Include more semantic keywords and related terms to improve topical relevance.');
  }

  return recommendations;
};

/**
 * Perform comprehensive content analysis
 */
export const analyzeContent = (
  content: string,
  targetKeywords: string[],
  baseUrl: string = ''
): ContentAnalysis => {
  const wordCount = (content.match(/\b\w+\b/g) || []).length;
  const readabilityScore = calculateReadabilityScore(content);
  const keywordAnalysis = analyzeKeywordDensity(content, targetKeywords);
  const semanticKeywords = generateSemanticKeywords(content, targetKeywords);
  const headingStructure = analyzeHeadingStructure(content, targetKeywords);
  const internalLinks = analyzeInternalLinks(content, targetKeywords, baseUrl);

  const analysis: ContentAnalysis = {
    wordCount,
    readabilityScore,
    keywordAnalysis,
    semanticKeywords,
    headingStructure,
    internalLinks,
    recommendations: [],
  };

  analysis.recommendations = generateSEORecommendations(analysis, targetKeywords);

  return analysis;
};

/**
 * Levenshtein distance calculation
 */
const levenshteinDistance = (str1: string, str2: string): number => {
  const matrix = Array(str2.length + 1).fill(null).map(() => Array(str1.length + 1).fill(null));
  
  for (let i = 0; i <= str1.length; i++) matrix[0][i] = i;
  for (let j = 0; j <= str2.length; j++) matrix[j][0] = j;
  
  for (let j = 1; j <= str2.length; j++) {
    for (let i = 1; i <= str1.length; i++) {
      const indicator = str1[i - 1] === str2[j - 1] ? 0 : 1;
      matrix[j][i] = Math.min(
        matrix[j][i - 1] + 1,
        matrix[j - 1][i] + 1,
        matrix[j - 1][i - 1] + indicator
      );
    }
  }
  
  return matrix[str2.length][str1.length];
};

/**
 * Optimize content for target keywords
 */
export const optimizeContentForKeywords = (
  content: string,
  targetKeywords: string[],
  targetDensity: number = 1.5
): { optimizedContent: string; changes: string[] } => {
  const analysis = analyzeContent(content, targetKeywords);
  const changes: string[] = [];
  let optimizedContent = content;

  // Add keywords to headings if missing
  analysis.headingStructure.forEach(heading => {
    if (!heading.hasKeyword && heading.level <= 3) {
      const primaryKeyword = targetKeywords[0];
      if (primaryKeyword) {
        const newHeading = `${heading.text} - ${primaryKeyword}`;
        optimizedContent = optimizedContent.replace(heading.text, newHeading);
        changes.push(`Added keyword "${primaryKeyword}" to ${heading.level === 1 ? 'H1' : `H${heading.level}`} heading`);
      }
    }
  });

  // Suggest keyword placement improvements
  targetKeywords.forEach(keyword => {
    const keywordAnalysis = analysis.keywordAnalysis.find(k => k.keyword === keyword);
    if (keywordAnalysis && keywordAnalysis.density < targetDensity) {
      changes.push(`Consider adding "${keyword}" ${Math.ceil((targetDensity - keywordAnalysis.density) * analysis.wordCount / 100)} more times`);
    }
  });

  return { optimizedContent, changes };
};